// mirror of auware/.../frontend/src/components/ui/Modal.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, useEffect, type HTMLAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Size presets for the modal dialog. */
type ModalSize = "sm" | "md" | "lg";

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  /** Whether the modal is visible. */
  isOpen: boolean;
  /** Called when the user requests to close the modal (overlay click or X). */
  onClose: () => void;
  /** Title rendered in the modal header. */
  title: string;
  /** Size preset. Defaults to `"md"`. */
  size?: ModalSize;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Dialog overlay with a centered white card, header, and body.
 *
 * ```tsx
 * <Modal isOpen={open} onClose={() => setOpen(false)} title="Confirm">
 *   Are you sure?
 * </Modal>
 * ```
 */
const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    { className = "", isOpen, onClose, title, size = "md", children, ...props },
    ref,
  ) => {
    // Close on Escape key
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose();
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      >
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          className={`w-full ${sizeClasses[size]} rounded-lg border border-neutral-200 bg-white shadow-lg ${className}`}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
            <h2
              id="modal-title"
              className="text-lg font-semibold text-neutral-900"
            >
              {title}
            </h2>
            <button
              type="button"
              className="rounded-lg p-1 text-neutral-400 transition-colors hover:text-neutral-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4">{children}</div>
        </div>
      </div>
    );
  },
);

Modal.displayName = "Modal";

export default Modal;
